import { FC, useMemo } from 'react';

import { LockClosedIcon } from '@heroicons/react/outline';
import {
  GiftDeliveryForStore,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import {
  getDeliveryShareStatusLabel,
  getDeliveryShareStatusStyle,
} from '../utils/giftOrderCoupon.utils';
import { ShareStatusBadge } from './ShareStatusBadge';

interface IndividualDeliveryRowProps {
  delivery: GiftDeliveryForStore;
  onShare: (delivery: GiftDeliveryForStore) => void;
  onMemo: (delivery: GiftDeliveryForStore) => void;
  /**
   * 잠금 번호 수정/추가 모달 오픈. 공유된 배송 또는 회수로 잠금된 배송에서 노출.
   */
  onOpenPhoneLockEdit?: (delivery: GiftDeliveryForStore) => void;
  isSharing: boolean;
}

const formatConfirmedAt = (date: Date | string) => {
  const d = new Date(date);
  return `${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')} 완료`;
};

export const IndividualDeliveryRow: FC<IndividualDeliveryRowProps> = ({
  delivery,
  onShare,
  onMemo,
  onOpenPhoneLockEdit,
  isSharing,
}) => {
  const giftItem = delivery.giftItem;
  const imageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItem?.giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem]);

  const itemName = typeof giftItem === 'object' ? giftItem?.name || '' : '';
  const isShared = delivery.shareStatus !== GiftDeliveryShareStatus.NotShared;
  const isAddressSubmitted =
    delivery.deliveryStatus !== GiftDeliveryStatus.WaitingAddress;

  // 잠금 진입점 노출 조건:
  //   - 아직 배송지 미입력(=수령자 인증 전이라 잠금이 의미 있음)
  //   - 공유됐거나(예방 잠금) / 이미 잠금 번호가 있거나(사후 수정)
  const lockedPhone = delivery.additionalRecipientPhone;
  // 수령자가 이미 본인 번호를 인증·등록한 경우(recipient.phoneNumber 존재)에는
  // 수신번호를 잠그거나 변경할 수 없으므로 진입점을 노출하지 않는다.
  const hasVerifiedRecipient = !!delivery.recipient?.phoneNumber;
  const showLockEntry =
    !!onOpenPhoneLockEdit &&
    !isAddressSubmitted &&
    !hasVerifiedRecipient &&
    (!!lockedPhone || isShared);

  return (
    <div
      className={`rounded-lg bg-white p-3 shadow-sm ${
        isShared ? 'ring-1 ring-green-400' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
          {imageUrl ? (
            <img
              src={fileBucketUrl(imageUrl, 'w200')}
              alt={itemName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
              No Image
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">#{delivery.seq}</span>
            <ShareStatusBadge
              label={getDeliveryShareStatusLabel(delivery.shareStatus)}
              styleClass={getDeliveryShareStatusStyle(delivery.shareStatus)}
              channel={delivery.lastSharedChannel}
              kakaoCount={delivery.shareKakaoCount}
            />
          </div>
          <p className="mt-0.5 truncate text-sm font-medium text-gray-800">
            {itemName}
            {delivery.quantity > 1 && (
              <span className="ml-1 text-xs text-gray-400">x{delivery.quantity}</span>
            )}
          </p>
        </div>

        {isShared && delivery.confirmedAt ? (
          <div className="flex flex-shrink-0 flex-col items-end gap-1">
            {delivery.memo ? (
              <button
                onClick={() => onMemo(delivery)}
                className="max-w-[140px] truncate rounded-lg bg-purple-50 px-4 py-2 text-sm font-medium text-purple-600 active:bg-purple-100"
              >
                {delivery.memo}
              </button>
            ) : (
              <button
                onClick={() => onMemo(delivery)}
                className="rounded-lg border border-dashed border-purple-300 px-4 py-2 text-sm text-purple-400 active:bg-purple-50"
              >
                메모 남기기
              </button>
            )}
            <span className="text-[10px] text-gray-400">
              {formatConfirmedAt(delivery.confirmedAt)}
            </span>
          </div>
        ) : (
          !isAddressSubmitted && (
            <button
              onClick={() => onShare(delivery)}
              disabled={isSharing}
              className={`flex-shrink-0 rounded-xl px-5 py-2.5 text-sm font-bold transition ${
                isShared
                  ? 'bg-green-100 text-green-700'
                  : 'bg-purple-600 text-white active:bg-purple-700'
              } ${isSharing ? 'opacity-50' : ''}`}
            >
              {isSharing ? '...' : isShared ? '다시 전달' : '전달하기'}
            </button>
          )
        )}
      </div>

      {showLockEntry && (
        <button
          onClick={() => onOpenPhoneLockEdit?.(delivery)}
          className={`mt-2.5 flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-xs active:opacity-80 ${
            lockedPhone
              ? 'bg-amber-50 text-amber-800'
              : 'border border-dashed border-gray-300 text-gray-500'
          }`}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <LockClosedIcon
              className={`h-3.5 w-3.5 flex-shrink-0 ${
                lockedPhone ? 'text-amber-600' : 'text-gray-400'
              }`}
            />
            {lockedPhone ? (
              <span className="truncate">
                <span className="font-bold">
                  {phoneNumberStringFormator(lockedPhone)}
                </span>
                <span className="ml-1 text-amber-600">님만 받을 수 있어요</span>
              </span>
            ) : (
              <span>받는 분 지정하기</span>
            )}
          </span>
          <span
            className={`flex-shrink-0 text-[11px] font-medium ${
              lockedPhone ? 'text-amber-700 underline' : 'text-gray-500 underline'
            }`}
          >
            {lockedPhone ? '변경' : '지정'}
          </span>
        </button>
      )}
    </div>
  );
};
